import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowUturnLeftIcon,
  BuildingOffice2Icon,
  HomeIcon,
  LifebuoyIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

import RovquestLogo from '../../assets/images/Rovquest_logo.svg';
import NavbarLink from 'components/layout/NavbarLink';
import Profile from 'components/layout/Profile';
import { getRoles, hasRole } from 'config/session';
import {
  BASE_PATH,
  OPERATOR_COMPANIES_PATH,
  OPERATOR_OPERATORS_PATH,
  OPERATOR_PATH,
  OPERATOR_SUPPORT_PATH,
} from 'router/pathName';

/**
 * YAC-2026-009 PR5a。運営の画面の枠（メニューとプロフィール）。
 *
 * 自社の画面の枠（components/layout/Layout.tsx）は会社の時間帯（プロフィールの company_info）を読んでから
 * 描くので、会社の無い運営だけのアカウントでは使わない。運営の画面は会社をまたぐので、会社の時間帯の
 * 注記も出さない。メニューは「ダッシュボード」「会社の管理」「運営の管理」（PR5b）。
 * 管理者も兼ねるアカウントには、自社の画面へ戻る項目を出す。
 */
const OperatorLayout: React.FC = () => {
  const { t } = useTranslation('layout');
  const location = useLocation();
  const managerToo = hasRole(getRoles(), 'manager');

  return (
    <main className="flex">
      <div className="w-[20%] bg-primary/300 h-screen">
        <div className="h-full flex flex-col overflow-y-auto" data-sidebar-scroll>
          <a href={OPERATOR_PATH} className="p-4 shrink-0" aria-label={t('operator.home-link')}>
            <img src={RovquestLogo} alt="" />
          </a>
          <nav className="shrink-0 p-4" aria-label={t('operator.menu-label')}>
            <NavbarLink
              icon={<HomeIcon color="#30537E" className="w-6 h-6" aria-hidden="true" />}
              label={t('operator.dashboard')}
              link={OPERATOR_PATH}
              isSelected={location.pathname === OPERATOR_PATH}
            />
            {/* PR5b。会社の管理（一覧・登録・詳細）と運営の管理（一覧・招待・外す）。 */}
            <NavbarLink
              icon={<BuildingOffice2Icon color="#30537E" className="w-6 h-6" aria-hidden="true" />}
              label={t('operator.companies')}
              link={OPERATOR_COMPANIES_PATH}
              isSelected={location.pathname.startsWith(OPERATOR_COMPANIES_PATH)}
            />
            <NavbarLink
              icon={<UserGroupIcon color="#30537E" className="w-6 h-6" aria-hidden="true" />}
              label={t('operator.operators-menu')}
              link={OPERATOR_OPERATORS_PATH}
              isSelected={location.pathname.startsWith(OPERATOR_OPERATORS_PATH)}
            />
            {/* YAC-2026-009 PR7b。運営のサポートの一覧（どの会社・どの運営のものも）と「終わらせる」。 */}
            <NavbarLink
              icon={<LifebuoyIcon color="#30537E" className="w-6 h-6" aria-hidden="true" />}
              label={t('support.menu')}
              link={OPERATOR_SUPPORT_PATH}
              isSelected={location.pathname.startsWith(OPERATOR_SUPPORT_PATH)}
            />
            {managerToo ? (
              <NavbarLink
                icon={<ArrowUturnLeftIcon color="#30537E" className="w-6 h-6" aria-hidden="true" />}
                label={t('manager-menu')}
                link={BASE_PATH}
                isSelected={false}
              />
            ) : null}
          </nav>
          <div className="mt-auto shrink-0 p-4">
            <Profile />
          </div>
        </div>
      </div>
      <article className="w-full h-screen bg-primary/50 overflow-y-scroll">
        <Outlet />
      </article>
    </main>
  );
};

export default OperatorLayout;
